import { SendHorizontalIcon, Trash2Icon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { isRefusal, useApiErrorMessage } from '@/api/errors'
import { addLeadNote, deleteLeadNote, type LeadNote } from '@/api/leads'
import { Role } from '@/auth/roles'
import { useAuth } from '@/auth/useAuth'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useColleagues } from '@/hooks/useColleagues'
import { formatDateTime } from '@/lib/dates'
import { userLabel } from '@/lib/userDisplay'

/**
 * The running record on a lead: who wrote what, and when.
 *
 * Appended, never overwritten — once a lead has been handed on, two people are writing on it, and
 * an edit would take one of them out of the conversation. That is a backend rule; this screen just
 * has to make it visible.
 *
 * Deleting is the one way back out, and it is deliberately narrow: whoever wrote a note may take
 * it back, and an administrator may tidy up after anybody. The backend applies the same rule, so
 * leaving the button off everywhere else is a convenience rather than the guard.
 *
 * Only on a lead that exists. Before the first save there is no id to hang a note on, and the
 * conversation itself belongs in the summary above.
 */
export function LeadNotes({ leadId, notes }: { leadId: string; notes: LeadNote[] }) {
  const { t } = useTranslation()
  const errorMessage = useApiErrorMessage()
  const { hasRole, user: me } = useAuth()
  const colleagues = useColleagues(true)
  const [written, setWritten] = useState<LeadNote[]>([])
  /** Gone from the server. The `notes` prop belongs to the lead the dialog was opened with. */
  const [removed, setRemoved] = useState<ReadonlySet<string>>(() => new Set())
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  /** The note a removal is waiting on — deleting one cannot be undone. */
  const [removing, setRemoving] = useState<LeadNote | null>(null)

  const all = [...notes, ...written].filter((note) => !removed.has(note.id))
  const isAdmin = hasRole(Role.ADMIN)

  function author(id: string): string {
    const colleague = colleagues.find((one) => one.id === id)
    // A raw id says nothing to a reader, and an account that has left the company keeps its notes.
    return colleague ? userLabel(colleague, t('leadForm.notes.unknownAuthor')) : t('leadForm.notes.unknownAuthor')
  }

  /** What the backend will allow. A button only where pressing it would actually go through. */
  function mayDelete(note: LeadNote): boolean {
    return isAdmin || note.authorId === me?.id
  }

  async function add() {
    const body = text.trim()
    if (busy || body === '') {
      return
    }
    setBusy(true)
    try {
      const saved = await addLeadNote(leadId, body)
      // Appended locally rather than reloading the lead: the dialog holds unsaved edits in every
      // other field, and re-fetching would be the one action in here that discards them.
      setWritten((current) => [...current, saved])
      setText('')
    } catch (error) {
      toast.error(errorMessage(error, t('leadForm.notes.failed')))
    } finally {
      setBusy(false)
    }
  }

  /**
   * Straight to the server, exactly like adding one: a note is not part of the form's unsaved
   * state, so Cancel neither undoes this nor is expected to.
   */
  async function remove(note: LeadNote) {
    if (busy) {
      return
    }
    setBusy(true)
    setRemoving(null)
    try {
      await deleteLeadNote(leadId, note.id)
    } catch (error) {
      // Already gone is the outcome that was asked for, not a failure to report — somebody else
      // deleted it while this dialog was open.
      if (!isRefusal(error, 'LEAD_NOTE_NOT_FOUND')) {
        toast.error(errorMessage(error, t('leadForm.notes.deleteFailed')))
        return
      }
    } finally {
      setBusy(false)
    }
    // Off the list either way: in both cases the row is no longer on the server.
    setRemoved((current) => new Set(current).add(note.id))
    setWritten((current) => current.filter((one) => one.id !== note.id))
  }

  return (
    <div className="grid gap-3">
      {all.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('leadForm.notes.empty')}</p>
      ) : (
        <ul className="grid gap-2">
          {all.map((note) => (
            <li key={note.id} className="bg-muted/40 flex items-start gap-2 rounded-lg border p-3">
              <div className="min-w-0 flex-1">
                <p className="text-muted-foreground flex flex-wrap gap-x-2 text-xs">
                  <span className="font-medium">{author(note.authorId)}</span>
                  <span>{formatDateTime(note.createdAt)}</span>
                </p>
                {/* The text is written in a textarea, so its line breaks are part of what was said. */}
                <p className="mt-1 text-sm whitespace-pre-wrap">{note.text}</p>
              </div>
              {mayDelete(note) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  disabled={busy}
                  // Several notes mean several identical buttons. Which one is only obvious to
                  // somebody who can see where it sits.
                  aria-label={t('leadForm.notes.deleteLabel', {
                    date: formatDateTime(note.createdAt),
                  })}
                  onClick={() => setRemoving(note)}
                >
                  <Trash2Icon />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="grid gap-2">
        <Textarea
          id="lead-note"
          rows={2}
          value={text}
          placeholder={t('leadForm.notes.placeholder')}
          aria-label={t('leadForm.notes.add')}
          onChange={(event) => setText(event.target.value)}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="justify-self-start"
          disabled={busy || text.trim() === ''}
          onClick={() => void add()}
        >
          <SendHorizontalIcon />
          {t('leadForm.notes.add')}
        </Button>
      </div>

      <AlertDialog open={removing !== null} onOpenChange={(open) => !open && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('leadForm.notes.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('leadForm.notes.deleteDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => removing && void remove(removing)}
            >
              {t('leadForm.notes.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
